/**
 * Meeting Slice
 * Manages meeting state and actions
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getMeetings,
  getMeetingById,
  createMeeting as createMeetingApi,
  updateMeeting as updateMeetingApi,
  deleteMeeting as deleteMeetingApi,
  startSimulation as startSimulationApi,
  completeMeeting as completeMeetingApi,
  getMeetingCollections,
} from "@/services/api/endpoints/meetings.api";
import {
  submitPreparation,
  checkPoolingStatus,
  fetchPreparedExpressions,
  fetchTopExpressions,
} from "@/services/api/endpoints/meeting-preparation.api";
import { MeetingState, Meeting, MeetingCollection } from "@/types/store.types";
import { RootState } from "@/store";
import {
  PreparationAnswer,
  PreparedExpression,
} from "@/types/meeting-preparation.types";
import { MeetingSessionManager } from "@/utils/meetingSessionManager";
import { isIOS } from "@/utils/platformDetection";
import { logger } from "@/utils/logger"
// Cache duration: 10 minutes (para otimizar home)
const CACHE_DURATION = 10 * 60 * 1000;

/**
 * Initial state
 */
const initialState: MeetingState = {
  meetings: [],
  currentMeeting: null,
  collections: [],
  collectionsLoading: false,
  collectionsError: null,
  collectionsLastFetched: null,
  loading: false,
  error: null,
  preparationSubmission: {
    status: "idle",
    expressions: null,
    audioBlobUrls: {},
    audioLoadingProgress: 0,
    error: null,
    poolingAttempts: 0,
    currentModuleId: null,
  },
};

/**
 * Async Thunks
 */

/**
 * Fetch meeting collections with cache
 * Força refresh se forceRefresh=true ou se cache expirou
 */
export const fetchMeetingCollections = createAsyncThunk(
  "meeting/fetchCollections",
  async (forceRefresh: boolean | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { collectionsLastFetched, collections } = state.meeting;
      const shouldForceRefresh = forceRefresh ?? false;

      logger.log("[fetchMeetingCollections] Starting fetch:", {
        forceRefresh: shouldForceRefresh,
        cacheAge: collectionsLastFetched
          ? Date.now() - collectionsLastFetched
          : null,
        hasCache: collections.length > 0,
      });

      // Verifica cache: se tem dados e cache válido, retorna do cache
      if (
        !shouldForceRefresh &&
        collectionsLastFetched &&
        collections.length > 0 &&
        Date.now() - collectionsLastFetched < CACHE_DURATION
      ) {
        logger.log("[fetchMeetingCollections] Using cache");
        return { collections, fromCache: true };
      }

      // Busca do backend
      logger.log(
        "[fetchMeetingCollections] Fetching from backend: /teacher/meeting/collections/meeting"
      );
      const data = await getMeetingCollections();
      logger.log("[fetchMeetingCollections] Backend response:", data);
      return { collections: data, fromCache: false };
    } catch (error: any) {
      logger.error("[fetchMeetingCollections] Error:", error);
      return rejectWithValue(
        error.message || "Failed to fetch meeting collections"
      );
    }
  }
);

/**
 * Fetch all meetings
 */
export const fetchMeetings = createAsyncThunk(
  "meeting/fetchAll",
  async (
    params: { page?: number; pageSize?: number; status?: string } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await getMeetings(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch meetings");
    }
  }
);

/**
 * Fetch meeting by ID
 */
export const fetchMeetingById = createAsyncThunk(
  "meeting/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const meeting = await getMeetingById(id);
      return meeting;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch meeting");
    }
  }
);

/**
 * Create new meeting
 */
export const createMeeting = createAsyncThunk(
  "meeting/create",
  async (data: Partial<Meeting>, { rejectWithValue }) => {
    try {
      const meeting = await createMeetingApi(data);
      return meeting;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create meeting");
    }
  }
);

/**
 * Update meeting
 */
export const updateMeeting = createAsyncThunk(
  "meeting/update",
  async (
    { id, data }: { id: string; data: Partial<Meeting> },
    { rejectWithValue }
  ) => {
    try {
      const meeting = await updateMeetingApi(id, data);
      return meeting;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update meeting");
    }
  }
);

/**
 * Delete meeting
 */
export const deleteMeeting = createAsyncThunk(
  "meeting/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteMeetingApi(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete meeting");
    }
  }
);

/**
 * Start meeting simulation
 */
export const startSimulation = createAsyncThunk(
  "meeting/startSimulation",
  async (id: string, { rejectWithValue }) => {
    try {
      const result = await startSimulationApi(id);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to start simulation");
    }
  }
);

/**
 * Complete meeting
 */
export const completeMeeting = createAsyncThunk(
  "meeting/complete",
  async (id: string, { rejectWithValue }) => {
    try {
      const meeting = await completeMeetingApi(id);
      return meeting;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to complete meeting");
    }
  }
);

/**
 * Submit meeting preparation (3-step async flow)
 * STEP 1: Submit answers
 * STEP 2: Pooling until finished
 * STEP 3: Fetch expressions with audio URLs
 */
export const submitMeetingPreparation = createAsyncThunk(
  "meeting/submitPreparation",
  async (
    payload: {
      moduleId: string;
      answers: PreparationAnswer[];
      meetingId: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { moduleId, answers, meetingId } = payload;

      // Get meeting session UUID
      const meetingSession = MeetingSessionManager.get(meetingId);

      // Get meeting metadata
      const metadata = MeetingSessionManager.getMetadata(meetingId);

      if (!meetingSession) {
        logger.error(
          "[submitMeetingPreparation] No meeting session found for meetingId:",
          meetingId
        );
        throw new Error(
          "Meeting session not found. Please restart from the meeting page."
        );
      }

      if (!metadata) {
        logger.error(
          "[submitMeetingPreparation] No metadata found for meetingId:",
          meetingId
        );
        throw new Error(
          "Meeting metadata not found. Please restart from the meeting page."
        );
      }

      // STEP 1: Submit preparation answers
      logger.log("[submitMeetingPreparation] Step 1: Submitting answers...");
      logger.log("[submitMeetingPreparation] Payload:", {
        moduleId,
        answersCount: answers.length,
        meetingSession,
        meetingName: metadata.name,
        meetingCategory: metadata.category,
      });

      const submitResponse = await submitPreparation({
        module_id: moduleId,
        answers,
        meeting_id: meetingSession,
        meeting_name: metadata.name,
        meeting_category: metadata.category,
      });

      logger.log("[submitMeetingPreparation] Submit response:", submitResponse);

      if (!submitResponse.need_pooling) {
        logger.error(
          "[submitMeetingPreparation] Expected need_pooling=true but got:",
          submitResponse
        );
        throw new Error("Expected need_pooling=true but got false");
      }

      logger.log("[submitMeetingPreparation] Step 2: Starting pooling...");

      // STEP 2: Pooling (40 attempts x 2s = 80s max)
      const MAX_ATTEMPTS = 40;
      const DELAY_MS = 2000;
      let attempts = 0;
      let finished = false;

      while (!finished && attempts < MAX_ATTEMPTS) {
        attempts++;

        // Update pooling attempts in state
        dispatch(updatePoolingAttempts(attempts));

        // Wait before next attempt
        await new Promise((resolve) => setTimeout(resolve, DELAY_MS));

        // Check status
        const poolingStatus = await checkPoolingStatus(
          moduleId,
          meetingSession
        );
        finished = poolingStatus.finished;

        logger.log(
          `[submitMeetingPreparation] Pooling attempt ${attempts}/${MAX_ATTEMPTS}: finished=${finished}`
        );
      }

      if (!finished) {
        throw new Error(
          "Processing timeout after 80 seconds. Please try again."
        );
      }

      // STEP 3: Fetch generated expressions
      logger.log("[submitMeetingPreparation] Step 3: Fetching expressions...");
      const expressions = await fetchPreparedExpressions(
        moduleId,
        meetingSession
      );

      logger.log(
        `[submitMeetingPreparation] Success! Received ${expressions.length} expressions`
      );
      logger.log("[submitMeetingPreparation] Expressions data:", expressions);

      return { expressions, moduleId };
    } catch (error: any) {
      logger.error("[submitMeetingPreparation] Error caught:", error);
      logger.error("[submitMeetingPreparation] Error type:", typeof error);
      logger.error(
        "[submitMeetingPreparation] Error keys:",
        Object.keys(error || {})
      );

      // Extrair mensagem de erro mais detalhada
      let errorMessage = "Failed to submit preparation. Please try again.";

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      logger.error(
        "[submitMeetingPreparation] Final error message:",
        errorMessage
      );

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch top expressions for dashboard session
 * Simpler flow than preparation (no pooling needed)
 */
export const fetchTopExpressionsFromDashboard = createAsyncThunk(
  "meeting/fetchTopExpressions",
  async (meetingSession: string, { dispatch, rejectWithValue }) => {
    try {
      logger.log(
        "[fetchTopExpressionsFromDashboard] Fetching expressions for session:",
        meetingSession
      );

      // Fetch expressions directly (no pooling needed)
      const expressions = await fetchTopExpressions(meetingSession);

      logger.log(
        `[fetchTopExpressionsFromDashboard] Success! Received ${expressions.length} expressions`
      );

      // Preload audio if we have expressions and wait for completion
      if (expressions && expressions.length > 0) {
        await dispatch(preloadAudioFiles(expressions)).unwrap();
      }

      return { expressions };
    } catch (error: any) {
      logger.error("[fetchTopExpressionsFromDashboard] Error:", error);

      let errorMessage = "Failed to fetch top expressions. Please try again.";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Preload audio files
 * - iOS: Store HTTP URLs directly (avoid Blob URL issues)
 * - Android/Desktop: Create Blob URLs for faster playback
 * Called after expressions are fetched
 */
export const preloadAudioFiles = createAsyncThunk(
  "meeting/preloadAudio",
  async (expressions: PreparedExpression[], { dispatch, rejectWithValue }) => {
    try {
      const platform = isIOS() ? "iOS" : "Android/Desktop";
      logger.log(
        `[preloadAudioFiles] Starting audio preload (${platform}) for`,
        expressions.length,
        "expressions"
      );
      const audioBlobUrls: Record<string, string> = {};
      let loaded = 0;
      const useDirectUrls = isIOS();

      for (const expression of expressions) {
        if (expression.audio_url) {
          try {
            if (useDirectUrls) {
              // iOS: Use HTTP URL directly (no Blob URL issues)
              logger.log(
                `[preloadAudioFiles] iOS: Using HTTP URL directly for ${expression.id}`
              );
              audioBlobUrls[expression.id] = expression.audio_url;
            } else {
              // Android/Desktop: Fetch and create Blob URL for faster playback
              const response = await fetch(expression.audio_url);
              if (!response.ok) {
                logger.warn(
                  `[preloadAudioFiles] Failed to fetch audio for expression ${expression.id}`
                );
                continue;
              }

              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              audioBlobUrls[expression.id] = blobUrl;
              logger.log(
                `[preloadAudioFiles] Android/Desktop: Created Blob URL for ${expression.id}`
              );
            }

            loaded++;
            const progress = Math.round((loaded / expressions.length) * 100);
            dispatch(updateAudioLoadingProgress(progress));

            logger.log(
              `[preloadAudioFiles] Loaded ${loaded}/${expressions.length} (${progress}%)`
            );
          } catch (error) {
            logger.warn(
              `[preloadAudioFiles] Error loading audio for expression ${expression.id}:`,
              error
            );
          }
        } else {
          logger.log(
            `[preloadAudioFiles] Expression ${expression.id} has no audio_url`
          );
          loaded++;
          const progress = Math.round((loaded / expressions.length) * 100);
          dispatch(updateAudioLoadingProgress(progress));
        }
      }

      logger.log(
        `[preloadAudioFiles] Completed (${platform})! Loaded`,
        Object.keys(audioBlobUrls).length,
        "audio files"
      );
      return audioBlobUrls;
    } catch (error: any) {
      logger.error("[preloadAudioFiles] Error:", error);
      return rejectWithValue(error.message || "Failed to preload audio files");
    }
  }
);

/**
 * Meeting Slice
 */
const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {
    /**
     * Clear error message
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Clear collections error
     */
    clearCollectionsError: (state) => {
      state.collectionsError = null;
    },

    /**
     * Set current meeting
     */
    setCurrentMeeting: (state, action: PayloadAction<Meeting | null>) => {
      state.currentMeeting = action.payload;
    },

    /**
     * Clear current meeting
     */
    clearCurrentMeeting: (state) => {
      state.currentMeeting = null;
    },

    /**
     * Reset meeting state
     */
    resetMeetings: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update pooling attempts counter
     */
    updatePoolingAttempts: (state, action: PayloadAction<number>) => {
      state.preparationSubmission.poolingAttempts = action.payload;
    },

    /**
     * Update audio loading progress (0-100)
     */
    updateAudioLoadingProgress: (state, action: PayloadAction<number>) => {
      state.preparationSubmission.audioLoadingProgress = action.payload;
    },

    /**
     * Reset preparation submission state
     */
    resetPreparationSubmission: (state) => {
      // Cleanup blob URLs before reset
      Object.values(state.preparationSubmission.audioBlobUrls).forEach(
        (url) => {
          try {
            URL.revokeObjectURL(url);
          } catch (error) {
            logger.warn(
              "[resetPreparationSubmission] Failed to revoke URL:",
              error
            );
          }
        }
      );

      state.preparationSubmission = {
        status: "idle",
        expressions: null,
        audioBlobUrls: {},
        audioLoadingProgress: 0,
        error: null,
        poolingAttempts: 0,
        currentModuleId: null,
      };
    },

    /**
     * Cleanup audio blob URLs (call on component unmount)
     */
    cleanupAudioBlobs: (state) => {
      Object.values(state.preparationSubmission.audioBlobUrls).forEach(
        (url) => {
          try {
            URL.revokeObjectURL(url);
          } catch (error) {
            logger.warn("[cleanupAudioBlobs] Failed to revoke URL:", error);
          }
        }
      );
      state.preparationSubmission.audioBlobUrls = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch meeting collections
      .addCase(fetchMeetingCollections.pending, (state) => {
        state.collectionsLoading = true;
        state.collectionsError = null;
      })
      .addCase(fetchMeetingCollections.fulfilled, (state, action) => {
        state.collectionsLoading = false;
        state.collections = action.payload.collections;
        // Atualiza timestamp apenas se buscou do backend
        if (!action.payload.fromCache) {
          state.collectionsLastFetched = Date.now();
        }
        state.collectionsError = null;
      })
      .addCase(fetchMeetingCollections.rejected, (state, action) => {
        state.collectionsLoading = false;
        state.collectionsError = action.payload as string;
      })

      // Fetch all meetings
      .addCase(fetchMeetings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetings.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
        state.error = null;
      })
      .addCase(fetchMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch meeting by ID
      .addCase(fetchMeetingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMeeting = action.payload;
        state.error = null;
      })
      .addCase(fetchMeetingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create meeting
      .addCase(createMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMeeting.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings.push(action.payload);
        state.currentMeeting = action.payload;
        state.error = null;
      })
      .addCase(createMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update meeting
      .addCase(updateMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMeeting.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.meetings.findIndex(
          (m) => m.id === action.payload.id
        );
        if (index !== -1) {
          state.meetings[index] = action.payload;
        }
        if (state.currentMeeting?.id === action.payload.id) {
          state.currentMeeting = action.payload;
        }
        state.error = null;
      })
      .addCase(updateMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete meeting
      .addCase(deleteMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMeeting.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = state.meetings.filter((m) => m.id !== action.payload);
        if (state.currentMeeting?.id === action.payload) {
          state.currentMeeting = null;
        }
        state.error = null;
      })
      .addCase(deleteMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Start simulation
      .addCase(startSimulation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startSimulation.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(startSimulation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Complete meeting
      .addCase(completeMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeMeeting.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.meetings.findIndex(
          (m) => m.id === action.payload.id
        );
        if (index !== -1) {
          state.meetings[index] = action.payload;
        }
        if (state.currentMeeting?.id === action.payload.id) {
          state.currentMeeting = action.payload;
        }
        state.error = null;
      })
      .addCase(completeMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Submit meeting preparation - pending
      .addCase(submitMeetingPreparation.pending, (state) => {
        state.preparationSubmission.status = "submitting";
        state.preparationSubmission.error = null;
        state.preparationSubmission.poolingAttempts = 0;
        state.preparationSubmission.audioLoadingProgress = 0;
      })
      // Submit meeting preparation - fulfilled
      .addCase(submitMeetingPreparation.fulfilled, (state, action) => {
        state.preparationSubmission.expressions = action.payload.expressions;
        state.preparationSubmission.currentModuleId = action.payload.moduleId;
        state.preparationSubmission.error = null;

        // If no expressions, go directly to success
        if (
          !action.payload.expressions ||
          action.payload.expressions.length === 0
        ) {
          state.preparationSubmission.status = "success";
        } else {
          state.preparationSubmission.status = "fetching";
        }
      })
      // Submit meeting preparation - rejected
      .addCase(submitMeetingPreparation.rejected, (state, action) => {
        state.preparationSubmission.status = "error";
        state.preparationSubmission.error = action.payload as string;
      })

      // Preload audio files - pending
      .addCase(preloadAudioFiles.pending, (state) => {
        state.preparationSubmission.status = "loading-audio";
        state.preparationSubmission.audioLoadingProgress = 0;
      })
      // Preload audio files - fulfilled
      .addCase(preloadAudioFiles.fulfilled, (state, action) => {
        state.preparationSubmission.status = "success";
        state.preparationSubmission.audioBlobUrls = action.payload;
        state.preparationSubmission.audioLoadingProgress = 100;
      })
      // Preload audio files - rejected
      .addCase(preloadAudioFiles.rejected, (state, action) => {
        // Even if audio loading fails, we still have expressions
        // So mark as success but log the error
        state.preparationSubmission.status = "success";
        logger.error("[meetingSlice] Audio preload failed:", action.payload);
      })

      // Fetch top expressions from dashboard - pending
      .addCase(fetchTopExpressionsFromDashboard.pending, (state) => {
        state.preparationSubmission.status = "submitting";
        state.preparationSubmission.error = null;
        state.preparationSubmission.poolingAttempts = 0;
        state.preparationSubmission.audioLoadingProgress = 0;
      })
      // Fetch top expressions from dashboard - fulfilled
      .addCase(fetchTopExpressionsFromDashboard.fulfilled, (state, action) => {
        state.preparationSubmission.expressions = action.payload.expressions;
        state.preparationSubmission.error = null;
        state.preparationSubmission.status = "success";
      })
      // Fetch top expressions from dashboard - rejected
      .addCase(fetchTopExpressionsFromDashboard.rejected, (state, action) => {
        state.preparationSubmission.status = "error";
        state.preparationSubmission.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCollectionsError,
  setCurrentMeeting,
  clearCurrentMeeting,
  resetMeetings,
  updatePoolingAttempts,
  updateAudioLoadingProgress,
  resetPreparationSubmission,
  cleanupAudioBlobs,
} = meetingSlice.actions;

export default meetingSlice.reducer;
